import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TimezoneSelect } from '../components/ui/timezone-select';
import { Calendar } from '../components/ui/calendar';
import { ArrowLeft, Clock, X } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Therapist } from '../types/user';
import { fetchAvailableSlots, bookAppointment, rescheduleAppointment, Appointment } from '../api/appointment';
import { formatDateToFrench, formatDateToYYYYMMDD } from '../utils/date';
import { authenticatedFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from './confirmation-modal';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  therapist: Therapist;
  existingAppointment?: Appointment; // Optional prop for rescheduling
}

const AppointmentModal = ({ isOpen, onClose, therapist, existingAppointment }: AppointmentModalProps) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(undefined);
  const [stage, setStage] = useState<'date' | 'time' | 'confirm' | 'profile'>('date');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeSlots, setTimeSlots] = useState<Array<{ time: string; iso?: string; available: boolean }>>([]);
  const [confirmedAppointment, setConfirmedAppointment] = useState<Appointment | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>('UTC');
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', timezone: 'Africa/Abidjan', phoneNumber: '', countryCode: '+225' });
  const [profileSaving, setProfileSaving] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.timezone) {
          setUserTimezone(userData.timezone);
        }
      } catch (e) {
        console.error('Error parsing user data', e);
      }
    }
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(undefined);
      setSelectedTime(undefined);
      setStage('date');
      setError(null);
      setTimeSlots([]);
      setConfirmedAppointment(null);
    }
  }, [isOpen]);

  const isProfileComplete = useCallback(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      return !!(user.firstName && user.firstName.trim());
    } catch { return false; }
  }, []);

  const handleClose = () => {
    onClose();
    setSelectedDate(undefined);
    setSelectedTime(undefined);
    setStage('date');
    setError(null);
    setTimeSlots([]);
    setConfirmedAppointment(null);
    setProfileForm({ firstName: '', lastName: '', timezone: 'Africa/Abidjan', phoneNumber: '', countryCode: '+225' });
    setProfileSaving(false);
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setError(null);
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/auth/complete-profile`, {
        method: 'POST',
        body: JSON.stringify({
          email: user.email,
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          timezone: profileForm.timezone,
          phoneNumber: profileForm.phoneNumber ? `${profileForm.countryCode}${profileForm.phoneNumber}` : undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('intake.error'));

      localStorage.setItem('token', data.token);
      data.user.type = 'patient';
      localStorage.setItem('user', JSON.stringify(data.user));
      setUserTimezone(profileForm.timezone);
      setProfileSaving(false);

      // Profile saved — now book the appointment directly
      if (!selectedDate || !selectedTime) return;
      setStage('confirm');
      setIsLoading(true);
      setError(null);
      try {
        let appointment;
        if (existingAppointment) {
          appointment = await rescheduleAppointment(existingAppointment.id, selectedDate, selectedTime);
        } else {
          appointment = await bookAppointment(therapist.userId, selectedDate, selectedTime);
        }
        setConfirmedAppointment(appointment);
      } catch (bookErr) {
        setError(bookErr instanceof Error ? bookErr.message : 'Erreur lors de la réservation');
      } finally {
        setIsLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      setProfileSaving(false);
    }
  };

  if (!isOpen) return null;

  const handleDateSelect = async (date: Date | undefined) => {
    setSelectedDate(date);
    setError(null);

    if (date) {
      setIsLoading(true);
      try {
        const slots = await fetchAvailableSlots(therapist.userId, date);
        setTimeSlots(slots);
        setStage('time');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement des disponibilités');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setError(null);
    setStage('confirm');
  };

  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime) return;

    // Gate: check profile completion before booking
    if (!isProfileComplete()) {
      setStage('profile');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let appointment;
      if (existingAppointment) {
        appointment = await rescheduleAppointment(existingAppointment.id, selectedDate, selectedTime);
      } else {
        appointment = await bookAppointment(therapist.userId, selectedDate, selectedTime);
      }

      setConfirmedAppointment(appointment);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la réservation');
    } finally {
      setIsLoading(false);
    }
  };

  const getSlotDisplayInfo = (slot: { time: string; iso?: string; available: boolean }) => {
    if (!slot.iso) return { time: slot.time, label: slot.time, fullDate: null };

    try {
      const date = new Date(slot.iso);
      const timeStr = new Intl.DateTimeFormat('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: userTimezone
      }).format(date);

      let label = timeStr;
      let fullDate = new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: userTimezone
      }).format(date);

      if (selectedDate) {
        const slotDateStr = new Intl.DateTimeFormat('en-CA', { // YYYY-MM-DD
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          timeZone: userTimezone
        }).format(date);

        const selectedDateStr = formatDateToYYYYMMDD(selectedDate);

        if (slotDateStr !== selectedDateStr) {
          const slotDateObj = new Date(slotDateStr);
          const selectedDateObj = new Date(selectedDateStr);
          const diffTime = slotDateObj.getTime() - selectedDateObj.getTime();
          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays > 0) label = `${timeStr} (+${diffDays}j)`;
          else if (diffDays < 0) label = `${timeStr} (${diffDays}j)`;
        }
      }

      return { time: timeStr, label, fullDate };
    } catch (e) {
      return { time: slot.time, label: slot.time, fullDate: null };
    }
  };

  const isRescheduling = !!existingAppointment;

  const handleBack = () => {
    setError(null);
    if (stage === 'time') setStage('date');
    else if (stage === 'confirm') setStage('time');
    else if (stage === 'profile') setStage('confirm');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            {stage !== 'date' && (
              <button
                onClick={handleBack}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Retour"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <h2 className="text-xl font-semibold">
              {isRescheduling ? 'Modifier le rendez-vous' : `Rendez-vous avec ${therapist.name}`}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {stage === 'date' && (
          <div className="space-y-4">
            <p className="text-gray-600">
              {isRescheduling
                ? 'Sélectionnez une nouvelle date pour votre consultation'
                : 'Sélectionnez une date pour votre consultation'}
            </p>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              className="rounded-md border"
              therapistId={therapist.userId}
              disabled={(date: Date) => {
                // Past dates
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (date < today) return true;

                // Weekends
                return date.getDay() === 0 || date.getDay() === 6;
              }}
            />
          </div>
        )}

        {stage === 'time' && selectedDate && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-gray-600 mb-4">
              <Clock size={20} />
              <span>
                {formatDateToFrench(selectedDate.toString())}
              </span>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00c4cc]"></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {timeSlots.length > 0 ? (
                  timeSlots.map((slot) => {
                    const { label } = getSlotDisplayInfo(slot);
                    return (
                      <Button
                        key={slot.time}
                        onClick={() => handleTimeSelect(slot.time)}
                        disabled={!slot.available}
                        variant={slot.available ? 'outline' : 'ghost'}
                        className={`h-12 ${!slot.available && 'text-gray-400 cursor-not-allowed'
                          }`}
                      >
                        {label}
                      </Button>
                    );
                  })
                ) : (
                  <div className="col-span-2 text-center text-gray-500 py-4">
                    Aucun créneau disponible pour cette date.
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {stage === 'confirm' && selectedDate && selectedTime && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="font-medium">Résumé de votre rendez-vous</h3>
              <div className="space-y-2 text-gray-600">
                {(() => {
                  const slot = timeSlots.find(s => s.time === selectedTime);
                  const { time, fullDate } = slot ? getSlotDisplayInfo(slot) : { time: selectedTime, fullDate: null };
                  return (
                    <>
                      <p>📅 {fullDate || formatDateToFrench(selectedDate)}</p>
                      <p>🕒 {time}</p>
                    </>
                  );
                })()}
                <p>👩‍⚕️ {therapist.name}</p>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleConfirm}
                className="w-full text-white"
                style={{ backgroundColor: '#00c4cc' }}
                disabled={isLoading}
              >
                {isLoading ? 'Confirmation en cours...' : (isRescheduling ? 'Confirmer la modification' : 'Confirmer le rendez-vous')}
              </Button>
            </div>
          </div>
        )}

        {stage === 'profile' && selectedDate && selectedTime && (
          <div className="space-y-5">
            <div className="text-center space-y-1">
              <div className="w-12 h-12 mx-auto bg-gradient-to-br from-[#00c4cc] to-[#00a3ab] rounded-full flex items-center justify-center mb-2">
                <span className="text-xl">👤</span>
              </div>
              <h3 className="font-semibold text-gray-800">{t('intake.bookingProfileTitle')}</h3>
              <p className="text-gray-500 text-sm">
                {t('intake.bookingProfileSubtitle')}
              </p>
            </div>

            {/* Show selected slot as reminder */}
            <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
              {(() => {
                const slot = timeSlots.find(s => s.time === selectedTime);
                const { time, fullDate } = slot ? getSlotDisplayInfo(slot) : { time: selectedTime, fullDate: null };
                return (
                  <p className="text-center">
                    📅 {fullDate || formatDateToFrench(selectedDate)} · 🕒 {time} · 👩‍⚕️ {therapist.name}
                  </p>
                );
              })()}
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-3">
              <Input
                type="text"
                placeholder={t('intake.firstNamePlaceholder')}
                value={profileForm.firstName}
                onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                autoFocus
              />
              <Input
                type="text"
                placeholder={t('intake.lastNamePlaceholder')}
                value={profileForm.lastName}
                onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
              />
              <div className="space-y-1">
                <label className="text-sm text-gray-500 flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </label>
                <div className="flex gap-2">
                  <select
                    className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                    value={profileForm.countryCode}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, countryCode: e.target.value }))}
                  >
                    <option value="+225">🇨🇮 +225</option>
                    <option value="+221">🇸🇳 +221</option>
                    <option value="+229">🇧🇯 +229</option>
                    <option value="+243">🇨🇩 +243</option>
                    <option value="+237">🇨🇲 +237</option>
                  </select>
                  <Input
                    type="tel"
                    placeholder="XX XX XX XX XX"
                    value={profileForm.phoneNumber}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setProfileForm(prev => ({ ...prev, phoneNumber: digits }));
                    }}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm text-gray-500">{t('intake.timezone')}</label>
                <TimezoneSelect
                  value={profileForm.timezone}
                  onValueChange={(value) => setProfileForm(prev => ({ ...prev, timezone: value }))}
                />
              </div>

              <Button
                type="submit"
                className="w-full text-white"
                style={{ backgroundColor: '#00c4cc' }}
                disabled={profileSaving || !profileForm.firstName.trim() || !profileForm.lastName.trim() || profileForm.phoneNumber.length < 8}
              >
                {profileSaving ? t('intake.saving') : t('intake.completeAndBook')}
              </Button>
            </form>
          </div>
        )}
      </div>

      {confirmedAppointment && (
        <ConfirmationModal
          isOpen={!!confirmedAppointment}
          onClose={() => {
            setConfirmedAppointment(null);
            handleClose();
            // Reload page or trigger refresh to show updated appointment
            window.location.reload();
          }}
          appointment={confirmedAppointment}
        />
      )}
    </div>
  );
};

export { AppointmentModal };